import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, MessageCircle, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ProductCard } from '@/components/ProductCard';
import { productos, Product, ProductPresentation } from '@/data/products';
import { useCart } from '@/context/CartContext';
import { useSeo, SITE_URL } from '@/hooks/useSeo';
import { productToAnalytics, trackEcommerce } from '@/utils/analytics';

export function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { dispatch } = useCart();
  
  const product = productos.find(p => p.slug === slug);
  const [selectedPresentation, setSelectedPresentation] = useState<ProductPresentation | null>(
    product?.presentaciones[0] || null
  );
  const [quantity, setQuantity] = useState(1);

  // SEO dinámico por producto (debe ir antes del early-return para respetar las reglas de hooks)
  useSeo(
    product
      ? {
          title: `${product.name} — Huaytanatura`,
          description: product.descripcion,
          path: `/producto/${product.slug}`,
          image: `${SITE_URL}${product.imagen}`,
          type: 'product',
        }
      : {}
  );

  // Datos estructurados schema.org/Product por producto
  useEffect(() => {
    if (!product) return;

    const prices = product.presentaciones.map((p) => p.precio);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'product-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.descripcion,
      image: `${SITE_URL}${product.imagen}`,
      category: product.categoria,
      brand: { '@type': 'Brand', name: 'Huaytanatura' },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'PEN',
        lowPrice: Math.min(...prices),
        highPrice: Math.max(...prices),
        offerCount: product.presentaciones.length,
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/producto/${product.slug}`,
      },
    });

    document.getElementById('product-jsonld')?.remove();
    document.head.appendChild(script);

    return () => {
      document.getElementById('product-jsonld')?.remove();
    };
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const presentation = product.presentaciones[0];
    trackEcommerce('view_item', [
      productToAnalytics(product, presentation.presentacion, presentation.precio),
    ]);
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Producto no encontrado</h1>
          <Button asChild>
            <Link to="/tienda">Volver a la tienda</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Related products from same category
  const relatedProducts = productos
    .filter(p => p.categoria === product.categoria && p.id !== product.id)
    .slice(0, 4);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(price);
  };

  const addToCart = () => {
    if (!selectedPresentation) return;

    trackEcommerce('add_to_cart', [
      productToAnalytics(product, selectedPresentation.presentacion, selectedPresentation.precio, quantity),
    ]);

    dispatch({
      type: 'ADD_ITEM',
      payload: {
        productId: product.id,
        name: product.name,
        slug: product.slug,
        presentation: selectedPresentation.presentacion,
        quantity,
        unitPrice: selectedPresentation.precio,
        image: product.imagen
      }
    });

    // Open cart to show the added item
    dispatch({ type: 'OPEN_CART' });
  };

  const handleWhatsAppPurchase = () => {
    if (selectedPresentation) {
      trackEcommerce(
        'generate_lead',
        [productToAnalytics(product, selectedPresentation.presentacion, selectedPresentation.precio, quantity)],
        { method: 'whatsapp', origen: 'ficha_producto' },
      );
    }

    const message = `Hola, quiero comprar:\n\n${product.name}\nPresentación: ${selectedPresentation?.presentacion}\nCantidad: ${quantity}\nPrecio: ${selectedPresentation ? formatPrice(selectedPresentation.precio * quantity) : ''}\n\n¿Podrían confirmar disponibilidad?`;
    
    window.open(
      `https://wa.me/+51915976931?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  // Mock nutritional info - you can make this dynamic per product
  const nutritionalInfo = [
    { nutrient: 'Energía', value: '450 kcal', per: '100g' },
    { nutrient: 'Proteína', value: '12g', per: '100g' },
    { nutrient: 'Grasas', value: '8g', per: '100g' },
    { nutrient: 'Carbohidratos', value: '65g', per: '100g' },
    { nutrient: 'Fibra', value: '5g', per: '100g' },
    { nutrient: 'Sodio', value: '2mg', per: '100g' }
  ];

  return (
    <div className="min-h-screen bg-background texture-paper">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/tienda">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a la tienda
            </Link>
          </Button>
          
          <nav className="text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary">Inicio</Link>
            <span className="mx-2">/</span>
            <Link to="/tienda" className="hover:text-primary">Tienda</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{product.name}</span>
          </nav>
        </div>

        {/* Product Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
          {/* Product Image */}
          <div className="space-y-4">
            <div className="aspect-square bg-muted rounded-lg overflow-hidden">
              {product.imagen ? (
                <img 
                  src={product.imagen} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                  <span className="text-6xl text-primary font-bold">
                    {product.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>

            {/* Additional images placeholder */}
            {product.imagenesAdicionales && (
              <div className="grid grid-cols-3 gap-2">
                {product.imagenesAdicionales.slice(0, 3).map((img, index) => (
                  <div key={index} className="aspect-square bg-muted rounded-lg">
                    <img 
                      src={img} 
                      alt={`${product.name} ${index + 2}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Category */}
            <Badge variant="secondary">{product.categoria}</Badge>

            {/* Title */}
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground">
              {product.name}
            </h1>

            {/* Tags */}
            <div className="flex flex-wrap gap-2">
              {product.etiquetas.map((etiqueta) => (
                <Badge key={etiqueta} variant="outline" className="capitalize">
                  {etiqueta}
                </Badge>
              ))}
            </div>

            {/* Description */}
            <div id="descripcion" className="scroll-mt-24">
              <p className="text-lg text-muted-foreground leading-relaxed">
                {product.descripcion}
              </p>
            </div>

            {/* Presentation Selector */}
            <div id="presentaciones" className="scroll-mt-24 space-y-3">
              <h3 className="font-semibold text-foreground">Presentación:</h3>
              <div className="grid grid-cols-3 gap-2">
                {product.presentaciones.map((presentation) => (
                  <button
                    key={presentation.presentacion}
                    onClick={() => setSelectedPresentation(presentation)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedPresentation?.presentacion === presentation.presentacion
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="font-medium">{presentation.presentacion}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatPrice(presentation.precio)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Price */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="text-3xl font-bold text-primary">
                {selectedPresentation ? formatPrice(selectedPresentation.precio) : 'Selecciona presentación'}
              </div>
              {selectedPresentation && (
                <p className="text-sm text-muted-foreground">
                  Precio por {selectedPresentation.presentacion}
                </p>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="space-y-3">
              <h3 className="font-semibold text-foreground">Cantidad:</h3>
              <div className="flex items-center space-x-3">
<Button
              variant="outline"
              size="sm"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              aria-label="Disminuir cantidad"
            >
              <Minus className="w-4 h-4" aria-hidden="true" />
            </Button>
                
                <span className="w-16 text-center font-medium text-lg">
                  {quantity}
                </span>
                
<Button
              variant="outline"
              size="sm"
              onClick={() => setQuantity(quantity + 1)}
              aria-label="Aumentar cantidad"
            >
              <Plus className="w-4 h-4" aria-hidden="true" />
            </Button>
              </div>

              {selectedPresentation && quantity > 1 && (
                <p className="text-sm text-muted-foreground">
                  Total: {formatPrice(selectedPresentation.precio * quantity)}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={addToCart}
                disabled={!selectedPresentation}
                className="w-full bg-primary hover:bg-primary-light text-lg py-3"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Añadir al carrito
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleWhatsAppPurchase}
                disabled={!selectedPresentation}
                className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground text-lg py-3"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Comprar por WhatsApp
              </Button>
            </div>
          </div>
        </div>

        {/* Nutritional Info */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Información Nutricional Referencial
          </h2>
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th className="text-left p-4 font-semibold">Nutriente</th>
                  <th className="text-left p-4 font-semibold">Por 100g / 100ml</th>
                </tr>
              </thead>
              <tbody>
                {nutritionalInfo.map((item, index) => (
                  <tr key={index} className="border-t border-border">
                    <td className="p-4">{item.nutrient}</td>
                    <td className="p-4 font-medium">{item.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            * Valores aproximados. Pueden variar según el lote y origen del producto.
          </p>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Productos relacionados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}